import TB from '@tiger/bridge';
import { useMount } from '@tiger/hooks';
import { Button } from 'antd';
import React, { FC, useCallback, useMemo } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { CardButtonWrap } from 'components/Card';
import PlaidLink from 'components/vendor/plaid/PlaidLink';
import completeIcon from 'static/images/icons/complete.png';
import failIcon from 'static/images/icons/fail.png';
import APP from 'stores/APP';
import classList from 'utils/classlist';
import { language } from 'utils/i18n';
import './style.scss';

const Complete: FC<{ isIRA?: boolean }> = ({ isIRA }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as {
        account_id: string;
        error_code: number;
        type: string;
    } | null;
    const { error_code, type } = locationState || {};
    // 13037 持卡人信息不匹配
    const status = error_code === 13037 ? 'errorName' : type;

    const statusMap = useMemo(
        () => ({
            error: {
                msg: __('We are unable to verify your bank account'),
                desc: __('Please try using another bank account'),
                link: '..'
            },
            errorName: {
                msg: __('The name on your account does not match the name on the selected bank account'),
                desc: __('Please select another bank account or contact your bank to update your bank information'),
                link: '..'
            },
            success: isIRA
                ? {
                      msg: __('Request received!'),
                      desc: __(
                          "Your distribution request has been submitted successfully. We'll inform you once the withdrawal has been processed."
                      ),
                      link: '/profile/withdraw',
                      btn: __('Done')
                  }
                : {
                      msg: __('Pending Withdraw Confirmation'),
                      desc: __('Your withdrawal request has been received! Please check the status in '),
                      link: '/profile/withdraw',
                      btn: __('Done')
                  }
        }),
        [isIRA]
    );

    const onConfirm = useCallback(() => {
        if (APP.isTiger) {
            if (type === 'success' && window.TigerBridge.quit) {
                TB.quit();
            } else {
                navigate(statusMap[status!].link);
            }
        } else {
            navigate(statusMap[status!].link);
        }
    }, [navigate, status, type, statusMap]);

    useMount(() => {
        window.addEventListener('popstate', onConfirm);

        return () => {
            window.removeEventListener('popstate', onConfirm);
        };
    });

    if (!locationState) {
        return <Navigate to=".." replace />;
    }

    return (
        <div className="withdraw-ach-complete-root">
            <img className="success-icon" src={type === 'success' ? completeIcon : failIcon} alt="" />
            <div
                className={classList('ach-text-body', {
                    'ach-text-normal': !APP.isTiger
                })}>
                <p className="ach-status">{statusMap[status!].msg}</p>
                {isIRA ? (
                    <p className="desc">{statusMap[status!].desc}</p>
                ) : (
                    <p className="desc">
                        {status === 'success' ? (
                            <>
                                {language === 'en_US' ? (
                                    <>
                                        <span>
                                            Your withdrawal request has been received! Please check the status in{' '}
                                        </span>
                                        <Link to="/profile/funding-records">Transfer History</Link>.
                                    </>
                                ) : (
                                    <>
                                        <span>您的出金请求已提交！请前往</span>
                                        <Link to="/profile/funding-records">出入金记录</Link>页面查看状态。
                                    </>
                                )}
                            </>
                        ) : (
                            statusMap[status!].desc
                        )}
                    </p>
                )}
                <CardButtonWrap className="btn-box">
                    {type !== 'error' && (
                        <Button block type="primary" size="large" className="btn-important" onClick={onConfirm}>
                            {statusMap[status!].btn}
                        </Button>
                    )}
                    {type === 'error' && (
                        <Link to={statusMap.error.link}>
                            <Button block type="primary" size="large" className="btn-important">
                                {__('Back')}
                            </Button>
                        </Link>
                    )}
                    {type !== 'success' && (
                        <PlaidLink
                            btnText={__('Add Another Bank Account')}
                            btnProps={{
                                type: 'primary',
                                size: 'large'
                            }}
                            source="withdraw"
                            errorInBtn
                        />
                    )}
                </CardButtonWrap>
            </div>
        </div>
    );
};

export default Complete;
